/* eslint-disable @next/next/no-img-element -- static Figma vector at its exact size */

import React from "react";
import { vercelStegaSplit } from "@vercel/stega";
import { cn } from "@/lib/utils";
import { Chip, Highlight } from "@/components/trademania-2/shared";

import check from "@/public/trademania-2-replay/check.svg";

/**
 * Renders a Sanity string whose `*starred*` parts are emphasised: `emphasis` wraps each of them
 * (highlight box, italic…). Keep line breaks with `whitespace-pre-line` on the parent.
 */
export function Emphasis({
  text,
  emphasis,
}: {
  text?: string;
  emphasis: (part: string) => React.ReactNode;
}) {
  if (!text) return null;
  // keep the invisible stega payload (visual editing) out of the split
  const { cleaned, encoded } = vercelStegaSplit(text);

  return (
    <>
      {cleaned
        .split(/\*([^*]+)\*/)
        .map((part, i) => (i % 2 ? <React.Fragment key={i}>{emphasis(part)}</React.Fragment> : part))}
      {encoded && <span className="sr-only">{encoded}</span>}
    </>
  );
}

/** Lime check + line, as in the step cards. */
export function Checklist({ items, className }: { items?: string[]; className?: string }) {
  if (!items?.length) return null;
  return (
    <ul className={cn("flex w-full flex-col gap-[1.5rem]", className)}>
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-center gap-[1.5rem]">
          <span className="flex h-[3rem] w-[3rem] shrink-0 items-center justify-center rounded-[3px] bg-[#9BE055]">
            <img
              src={check.src}
              alt=""
              width={16}
              height={16}
              className="h-[2rem] w-[2rem]"
            />
          </span>
          <span className="min-w-0 flex-1 font-geist text-[2rem] leading-[1.4] text-[#0B0C0D]/80">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Chip + headline above every step box; `highlightClassName` sets the box fill. */
export function StepSection({
  chip,
  title,
  highlightClassName,
  className,
  children,
}: {
  chip?: string;
  title?: string;
  highlightClassName?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex flex-col items-center gap-[3.5rem] px-[2rem] py-[7rem] lg:gap-[4rem] lg:px-0 lg:py-[12rem]",
        className,
      )}>
      <div className="flex flex-col items-center gap-[2rem] lg:gap-[2.5rem]">
        <Chip
          text={chip}
          slashes
        />
        <h2 className="text-center font-owners text-[4rem] leading-[1.1] font-medium tracking-[.0093em] text-[#0B0C0D] uppercase lg:text-[6rem] lg:whitespace-nowrap">
          <Emphasis
            text={title}
            emphasis={(part) => (
              // Figma: 1px lime sides, the box spans the line height
              <Highlight
                className="italic"
                boxClassName={cn(
                  "inset-x-[-.19em] inset-y-0 border-x lg:inset-x-[-.167em] lg:inset-y-0",
                  highlightClassName,
                )}>
                {part}
              </Highlight>
            )}
          />
        </h2>
      </div>
      {children}
    </section>
  );
}

/** White step card: copy column (499, 2 from the edge) and visual (683, 8 from the edge) from lg up, stacked on phones. */
export function StepCard({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <article
      className={cn(
        "grid w-full grid-cols-1 gap-[.75rem] rounded-[7px] border border-black/[.06] bg-white p-[.75rem] lg:w-[150rem] lg:grid-cols-[62.375rem_1fr] lg:items-center lg:gap-[1rem] lg:py-[.75rem] lg:pr-[.875rem] lg:pl-[.125rem]",
        className,
      )}>
      {children}
    </article>
  );
}

/** Card copy column: 20 × 28 padding on phones, 40 from lg up. */
export function StepCopy({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-[2.5rem] px-[2.5rem] py-[3.5rem] lg:p-[5rem]">{children}</div>
  );
}

/** Card headline: 28px on phones, 36px from lg up. */
export const cardTitleClass =
  "font-owners text-[3.5rem] leading-[1.1] font-medium tracking-[.0093em] text-[#0B0C0D] uppercase lg:text-[4.5rem]";

/** 16px label on phones, 18px from lg up (Figma CTAs). */
export const ctaPhoneLabelClass = "[&_.launch-button-text]:text-[2rem] lg:[&_.launch-button-text]:text-[2.25rem]";
